import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { OpenAPI } from "./client"
import { PostHogProvider } from 'posthog-js/react'
import { handleAuthError } from './lib/auth-utils'

// Configure the OpenAPI client
OpenAPI.BASE = import.meta.env.VITE_API_URL || "http://localhost:8000"
OpenAPI.TOKEN = () => localStorage.getItem("access_token") || ""

// Handle API errors (401/403 = logout and redirect to login)
const globalErrorHandler = (error: Error) => {
  console.log('🚨 Global error handler triggered:', {
    error,
    name: error.name,
    message: error.message,
    status: (error as any)?.status,
    errorType: typeof error,
    isApiError: error.name === 'ApiError'
  });
  
  // Use the centralized auth error handler
  if (handleAuthError(error)) {
    return;
  }
  
  console.log('⚠️ Error not handled by auth redirect:', { status: (error as any)?.status, error });
}

// Create QueryClient with error handling
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: globalErrorHandler,
  }),
  mutationCache: new MutationCache({
    onError: globalErrorHandler,
  }),
})

// Only enable PostHog in production environment
const isProduction = import.meta.env.VITE_ENVIRONMENT === "production"
const posthogConfig = isProduction ? {
  apiKey: import.meta.env.VITE_PUBLIC_POSTHOG_KEY,
  options: {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    defaults: '2025-05-24',
    capture_exceptions: true,
    debug: false,
    session_recording: {
      recordCrossOriginIframes: true,
    },
    capture_pageview: true,
    capture_pageleave: true,
  }
} : null

createRoot(document.getElementById("root")!).render(
  posthogConfig ? (
    <PostHogProvider
      apiKey={posthogConfig.apiKey}
      options={posthogConfig.options}
    >
      <QueryClientProvider client={queryClient}>
          <App />
      </QueryClientProvider>
    </PostHogProvider>
  ) : (
    <QueryClientProvider client={queryClient}>
        <App />
    </QueryClientProvider>
  )
);
